/* eslint-disable no-unused-vars, no-param-reassign */
import { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, FormHelperText, Tab, Tabs, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';

const CANVAS_HEIGHT = 200;
const MOBILE_CANVAS_HEIGHT = 150;
const FONT_LOADED_KEY = '__consent_dancing_script_loaded';

// Signature export settings — keep base64 small enough for DB index limits
const SIG_EXPORT_MAX_WIDTH = 600;
const SIG_EXPORT_QUALITY = 0.7;

// Pen thickness presets (logical pixels)
const PEN_SIZES = [
    { value: 1.5, label: 'S', title: 'Thin' },
    { value: 2.5, label: 'M', title: 'Medium' },
    { value: 4,   label: 'L', title: 'Thick' },
];

/**
 * Downscale a canvas to a max width and export as a compact JPEG data URL.
 * Uses an offscreen canvas at 1x pixel ratio to keep the base64 string small.
 */
function canvasToCompactDataUrl(sourceCanvas) {
    const srcW = sourceCanvas.width;
    const srcH = sourceCanvas.height;

    // Calculate scale-down factor (signatures don't need retina resolution in storage)
    const ratio = window.devicePixelRatio || 1;
    const logicalW = srcW / ratio;
    const logicalH = srcH / ratio;
    const scale = Math.min(1, SIG_EXPORT_MAX_WIDTH / logicalW);
    const outW = Math.round(logicalW * scale);
    const outH = Math.round(logicalH * scale);

    const offscreen = document.createElement('canvas');
    offscreen.width = outW;
    offscreen.height = outH;
    const ctx = offscreen.getContext('2d');
    // White background (JPEG has no transparency)
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, outW, outH);
    ctx.drawImage(sourceCanvas, 0, 0, outW, outH);

    return offscreen.toDataURL('image/jpeg', SIG_EXPORT_QUALITY);
}

const ConsentSignaturePad = ({ fieldId, label, value, onChange, error, allowDrawn, allowTyped, readOnly, borderStyle, borderColor, borderWidth, borderRadius, backgroundColor, required }) => {
    const [mode, setMode] = useState(allowDrawn ? 0 : 1); // 0 = draw, 1 = type
    const [typedName, setTypedName] = useState('');
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasDrawn, setHasDrawn] = useState(false);
    const [penThickness, setPenThickness] = useState(2.5);

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    // Stroke history for undo: array of {points: [{x, y}], lineWidth: number}
    const strokesRef = useRef([]);
    // The stroke currently being drawn
    const currentStrokeRef = useRef(null);

    // Load Dancing Script font for typed signatures
    useEffect(() => {
        if (window[FONT_LOADED_KEY]) return;
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        window[FONT_LOADED_KEY] = true;
    }, []);

    const getCanvasCtx = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas) return null;
        return canvas.getContext('2d');
    }, []);

    const setupCanvas = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const ratio = window.devicePixelRatio || 1;
        const width = container.offsetWidth;
        const height = width < 600 ? MOBILE_CANVAS_HEIGHT : CANVAS_HEIGHT;

        canvas.width = width * ratio;
        canvas.height = height * ratio;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const ctx = canvas.getContext('2d');
        ctx.scale(ratio, ratio);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = penThickness;
        ctx.strokeStyle = '#000';

        // Fill white background
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Replay all saved strokes onto the canvas (used after undo or resize).
     * Must be called AFTER setupCanvas() so the context is scaled correctly.
     */
    const redrawAllStrokes = useCallback((strokes) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.strokeStyle = '#000';

        strokes.forEach((stroke) => {
            if (!stroke.points || stroke.points.length < 2) return;
            ctx.lineWidth = stroke.lineWidth;
            ctx.beginPath();
            ctx.moveTo(stroke.points[0].x, stroke.points[0].y);
            for (let i = 1; i < stroke.points.length; i += 1) {
                ctx.lineTo(stroke.points[i].x, stroke.points[i].y);
            }
            ctx.stroke();
        });
    }, []);

    useEffect(() => {
        if (mode === 0) {
            setupCanvas();
        }
    }, [mode, setupCanvas]);

    useEffect(() => {
        const handleResize = () => {
            if (mode === 0) {
                strokesRef.current = [];
                setHasDrawn(false);
                setupCanvas();
                onChange(fieldId, '');
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [mode, setupCanvas, fieldId, onChange]);

    const getPointerPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return { x: clientX - rect.left, y: clientY - rect.top };
    };

    const startDraw = (e) => {
        e.preventDefault();
        const ctx = getCanvasCtx();
        if (!ctx) return;
        setIsDrawing(true);
        const pos = getPointerPos(e);
        ctx.lineWidth = penThickness;
        ctx.strokeStyle = '#000';
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(pos.x, pos.y);
        currentStrokeRef.current = { points: [pos], lineWidth: penThickness };
    };

    const draw = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const ctx = getCanvasCtx();
        if (!ctx) return;
        const pos = getPointerPos(e);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
        setHasDrawn(true);
        if (currentStrokeRef.current) {
            currentStrokeRef.current.points.push(pos);
        }
    };

    const endDraw = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        setIsDrawing(false);
        // Save completed stroke to history (only if it has more than one point)
        if (currentStrokeRef.current && currentStrokeRef.current.points.length > 1) {
            strokesRef.current = [...strokesRef.current, currentStrokeRef.current];
        }
        currentStrokeRef.current = null;
        const canvas = canvasRef.current;
        if (canvas && hasDrawn) {
            onChange(fieldId, canvasToCompactDataUrl(canvas));
        }
    };

    const clearCanvas = () => {
        strokesRef.current = [];
        setupCanvas();
        setHasDrawn(false);
        onChange(fieldId, '');
    };

    /**
     * Undo the last drawn stroke by replaying all prior strokes onto a fresh canvas.
     */
    const undoLastStroke = () => {
        const newStrokes = strokesRef.current.slice(0, -1);
        strokesRef.current = newStrokes;
        setupCanvas();
        if (newStrokes.length > 0) {
            redrawAllStrokes(newStrokes);
            const canvas = canvasRef.current;
            if (canvas) onChange(fieldId, canvasToCompactDataUrl(canvas));
        } else {
            setHasDrawn(false);
            onChange(fieldId, '');
        }
    };

    const handleTypedChange = (e) => {
        const name = e.target.value;
        setTypedName(name);

        if (!name.trim()) {
            onChange(fieldId, '');
            return;
        }

        // Render typed signature to a hidden canvas
        const hiddenCanvas = document.createElement('canvas');
        const ratio = window.devicePixelRatio || 1;
        const width = 400;
        const height = 100;
        hiddenCanvas.width = width * ratio;
        hiddenCanvas.height = height * ratio;
        const ctx = hiddenCanvas.getContext('2d');
        ctx.scale(ratio, ratio);
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, width, height);
        ctx.fillStyle = '#000';
        ctx.font = '32px "Dancing Script", cursive';
        ctx.textBaseline = 'middle';
        ctx.fillText(name, 20, height / 2);

        onChange(fieldId, canvasToCompactDataUrl(hiddenCanvas));
    };

    const handleModeChange = (_, newVal) => {
        if (newVal === null) return;
        setMode(newVal);
        strokesRef.current = [];
        onChange(fieldId, '');
        setTypedName('');
        setHasDrawn(false);
    };

    const showTabs = allowDrawn && allowTyped;
    // When only draw mode is configured but typed is also allowed, offer a "Type instead" link
    const showTypeToggle = allowTyped && !showTabs;

    if (readOnly) {
        return (
            <Box sx={{ mb: 2 }}>
                {value ? (
                    <Box
                        component="img"
                        src={value}
                        alt={label || 'Signature'}
                        sx={{
                            maxWidth: '100%',
                            maxHeight: 200,
                            border: '1px solid rgba(145,158,171,0.24)',
                            borderRadius: '8px',
                            p: 1,
                            bgcolor: '#fff',
                        }}
                    />
                ) : (
                    <Typography variant="body2" color="text.secondary">No signature provided</Typography>
                )}
            </Box>
        );
    }

    const isSigned = Boolean(value);

    const getBorderColor = () => {
        if (isSigned) return '#5DCAA5';
        if (error) return '#FF4842';
        return 'rgba(0,0,0,0.12)';
    };

    return (
        <Box sx={{
            mb: 2,
            border: `0.5px solid ${getBorderColor()}`,
            borderRadius: '12px',
            overflow: 'hidden',
            transition: 'border-color 0.25s ease',
        }}>
            {/* Header bar */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 1.75,
                py: 1,
                borderBottom: '0.5px solid rgba(0,0,0,0.07)',
                bgcolor: 'rgba(0,0,0,0.02)',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#111' }}>
                        {label || 'Patient signature'}
                    </Typography>
                    {required && (
                        <Typography component="span" sx={{ fontSize: '11px', color: '#E24B4A', fontWeight: 500 }}>
                            Required
                        </Typography>
                    )}
                </Box>
                {showTabs && (
                    <Tabs
                        value={mode}
                        onChange={handleModeChange}
                        sx={{
                            minHeight: 28,
                            '& .MuiTab-root': {
                                minHeight: 28, py: 0, px: 1.5, fontSize: '0.75rem',
                                textTransform: 'none', fontWeight: 500, minWidth: 0,
                            },
                            '& .MuiTabs-indicator': { height: 2 },
                        }}
                    >
                        <Tab label="Draw" />
                        <Tab label="Type instead" />
                    </Tabs>
                )}
                {showTypeToggle && (
                    <Typography
                        component="button"
                        onClick={() => handleModeChange(null, 1)}
                        sx={{
                            fontSize: '12px',
                            color: '#637381',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            fontFamily: 'inherit',
                            p: 0,
                            '&:hover': { color: '#212B36' },
                        }}
                    >
                        Type instead
                    </Typography>
                )}
            </Box>

            {(mode === 0 && allowDrawn) && (
                <Box ref={containerRef}>
                    {/* ── Draw toolbar ── */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.75, py: 0.5, flexWrap: 'wrap' }}>
                        {/* Pen thickness */}
                        <ToggleButtonGroup
                            value={penThickness}
                            exclusive
                            onChange={(_, v) => { if (v !== null) setPenThickness(v); }}
                            size="small"
                            sx={{
                                '& .MuiToggleButton-root': {
                                    px: 1, py: 0.125, fontSize: '0.68rem', fontWeight: 600,
                                    lineHeight: 1.6, minWidth: 24, border: '0.5px solid rgba(0,0,0,0.12)',
                                    color: 'text.secondary',
                                    '&.Mui-selected': { color: '#00AB55', bgcolor: 'rgba(0,171,85,0.08)', borderColor: 'rgba(0,171,85,0.3)' },
                                },
                            }}
                        >
                            {PEN_SIZES.map((ps) => (
                                <ToggleButton key={ps.value} value={ps.value} title={ps.title}>
                                    {ps.label}
                                </ToggleButton>
                            ))}
                        </ToggleButtonGroup>

                        <Box sx={{ flex: 1 }} />

                        {/* Undo — with undo arrow icon */}
                        <Button
                            size="small"
                            disabled={strokesRef.current.length === 0}
                            onClick={undoLastStroke}
                            startIcon={
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12.5 8c-2.65 0-5.05 1-6.9 2.6L2 7v9h9l-3.62-3.62c1.39-1.16 3.16-1.88 5.12-1.88 3.54 0 6.55 2.31 7.6 5.5l2.37-.78C21.08 11.03 17.15 8 12.5 8z"/>
                                </svg>
                            }
                            sx={{ color: 'text.secondary', textTransform: 'none', fontSize: '0.72rem', minWidth: 0, px: 0.75, py: 0 }}
                        >
                            Undo
                        </Button>

                        {/* Clear — with X icon */}
                        <Button
                            size="small"
                            disabled={!hasDrawn}
                            onClick={clearCanvas}
                            startIcon={
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                                </svg>
                            }
                            sx={{
                                color: hasDrawn ? 'text.secondary' : 'text.disabled',
                                textTransform: 'none', fontSize: '0.72rem', minWidth: 0, px: 0.75, py: 0,
                            }}
                        >
                            Clear
                        </Button>
                    </Box>

                    {/* Canvas with "Sign here" placeholder overlay */}
                    <Box sx={{ position: 'relative' }}>
                        {!hasDrawn && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    pointerEvents: 'none',
                                    textAlign: 'center',
                                    userSelect: 'none',
                                }}
                            >
                                {/* Pen icon */}
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ opacity: 0.25, display: 'block', margin: '0 auto 4px' }}>
                                    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="#637381"/>
                                </svg>
                                <Typography sx={{
                                    fontSize: '13px',
                                    color: '#B0B8C1',
                                    fontWeight: 400,
                                    letterSpacing: '0.01em',
                                }}>
                                    Sign here
                                </Typography>
                            </Box>
                        )}
                        <Box
                            component="canvas"
                            ref={canvasRef}
                            onMouseDown={startDraw}
                            onMouseMove={draw}
                            onMouseUp={endDraw}
                            onMouseLeave={endDraw}
                            onTouchStart={startDraw}
                            onTouchMove={draw}
                            onTouchEnd={endDraw}
                            sx={{
                                bgcolor: backgroundColor,
                                cursor: 'crosshair',
                                touchAction: 'none',
                                display: 'block',
                                width: '100%',
                                // Dashed border when canvas is empty, solid when drawing/drawn
                                border: hasDrawn || isDrawing
                                    ? `1.5px solid rgba(0,171,85,0.35)`
                                    : `2px dashed #DFE3E8`,
                                borderRadius: '0',
                                transition: 'border 0.2s ease',
                            }}
                        />
                    </Box>
                </Box>
            )}

            {(mode === 1 && allowTyped) && (
                <Box sx={{ p: 1.75 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Type your full name"
                        value={typedName}
                        onChange={handleTypedChange}
                        error={Boolean(error)}
                        sx={{ mb: 1, '& .MuiInputBase-root': { borderRadius: '8px', fontSize: '15px' } }}
                    />
                    {typedName && (
                        <Box sx={{
                            border: '0.5px solid rgba(0,0,0,0.12)',
                            borderRadius: '8px',
                            p: 2,
                            textAlign: 'center',
                            bgcolor: '#fafafa',
                            minHeight: 60,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <Typography sx={{ fontFamily: '"Dancing Script", cursive', fontSize: 32, color: '#000' }}>
                                {typedName}
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}

            {error && <Box sx={{ px: 1.75, pb: 0.5 }}><FormHelperText error>{error}</FormHelperText></Box>}

            {/* Status bar */}
            <Box sx={{
                px: 1.75,
                py: 0.75,
                borderTop: '0.5px solid rgba(0,0,0,0.07)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                bgcolor: isSigned ? 'rgba(0,171,85,0.04)' : 'transparent',
                transition: 'background-color 0.3s ease',
            }}>
                {isSigned && (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                        <circle cx="12" cy="12" r="10" fill="#00AB55" opacity="0.15"/>
                        <polyline points="7 12 10 15 17 9" stroke="#0F6E56" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                )}
                <Typography sx={{
                    fontSize: '11.5px',
                    color: isSigned ? '#0F6E56' : '#aaa',
                    fontWeight: isSigned ? 600 : 400,
                    transition: 'color 0.2s',
                }}>
                    {isSigned ? 'Signed ✓' : 'Sign with your finger or mouse'}
                </Typography>
            </Box>
        </Box>
    );
};

ConsentSignaturePad.propTypes = {
    fieldId: PropTypes.string.isRequired,
    label: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    error: PropTypes.string,
    allowDrawn: PropTypes.bool,
    allowTyped: PropTypes.bool,
    readOnly: PropTypes.bool,
    borderStyle: PropTypes.string,
    borderColor: PropTypes.string,
    borderWidth: PropTypes.number,
    borderRadius: PropTypes.number,
    backgroundColor: PropTypes.string,
    required: PropTypes.bool,
};

ConsentSignaturePad.defaultProps = {
    label: 'Signature',
    value: '',
    error: '',
    allowDrawn: true,
    allowTyped: true,
    readOnly: false,
    borderStyle: 'solid',
    borderColor: 'rgba(145,158,171,0.32)',
    borderWidth: 1,
    borderRadius: 12,
    backgroundColor: '#fff',
    required: false,
};

export default ConsentSignaturePad;
